import { ContextualInfoButtonWithText } from "@/components/buttons/ContextualInfoButton";
import ContextualInfoCollapse from "@/components/buttons/ContextualInfoCollapse";
import AdaptationTag from "@/components/display/AdaptationTag";
import { HazardPlusTotal, Year } from "@/features/pe_aldd";
import { PeaSummaryResponse, PeaSummaryRow } from "@/store/services/facades/types/pea";
import { ADAPTATION_TERM_COLORS } from "@/types/adaptation";
import { Rcp, RCP_COLORS, rcpValues } from "@/types/rcp";
import { useAdaptationPermissions } from "@/utils/hooks/useAdaptationPermissions";
import { Empty } from "antd";
import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useCollectionState } from "../context/CollectionContext";
import { useMetricOptions } from "../hooks/useMetricOptions";
import { CompareResultsMode, PointDetailsPermissions } from "../types";
import { ChartHeader, Header } from "./charts/common/Text";
import { ChartData, ChartDataPoint } from "./charts/common/types";
import { formatYAxisTick, getMetricShortLabel, isHazardMetricDataAvailable, MetricKey, multiplierForMetric, PERCENT_METRICS } from "./charts/common/utils";
import { ScenarioLineChart } from "./charts/ScenarioLineChart";
import ScenarioMiniRangeGrid from "./charts/ScenarioMiniRangeGrid";
import CollectionFiltersLabel from "./CollectionFiltersLabel";
import SelectMetricOptions from "./SelectMetricOptions";
type PortfolioRow = PeaSummaryRow & { [K in MetricKey]: Record<string, number> };
const extractScenarios = (rows: PeaSummaryResponse): Rcp[] => {
  const scenarioSet = new Set<Rcp>();
  rows.forEach(row => {
    if (rcpValues.includes(row.climate_scenario as Rcp)) {
      scenarioSet.add(row.climate_scenario as Rcp);
    }
  });
  return Array.from(scenarioSet);
};
const extractYears = (rows: PeaSummaryResponse, field: MetricKey): number[] => {
  const yearSet = new Set<number>();
  rows.forEach(row => {
    const fieldData = (row as PortfolioRow)[field];
    if (fieldData) {
      Object.keys(fieldData).forEach(year => yearSet.add(Number(year)));
    }
  });
  return Array.from(yearSet).sort((a, b) => a - b);
};
const buildChartDataPoint = (year: number, scenarios: Rcp[], rows: PeaSummaryResponse, field: MetricKey): ChartDataPoint => {
  const point: ChartDataPoint = {
    year
  };
  const multiplier = multiplierForMetric(field);
  const scenarioToRow = new Map<Rcp, PeaSummaryRow>();
  rows.forEach(r => {
    const scenarioKey = r.climate_scenario as Rcp;
    if (scenarioKey) {
      scenarioToRow.set(scenarioKey, r);
    }
  });
  scenarios.forEach(scenario => {
    const row = scenarioToRow.get(scenario);
    if (!row) return;
    const fieldData = (row as PortfolioRow)[field];
    const value = fieldData?.[year as Year];
    point[scenario] = value * multiplier;
  });
  return point;
};
const buildChartData = (rows: PeaSummaryResponse, field: MetricKey): ChartData => {
  if (!rows.length) {
    return {
      chartData: [],
      scenarios: []
    };
  }
  const scenarios = extractScenarios(rows);
  const years = extractYears(rows, field);
  const chartData = years.map(year => buildChartDataPoint(year, scenarios, rows, field));
  return {
    chartData,
    scenarios
  };
};
const filterRowsByHazard = (data: PeaSummaryResponse, hazard: HazardPlusTotal): PeaSummaryResponse => {
  return data.filter(row => {
    const rowHazard = (row as PortfolioRow).hazard;
    return hazard === "total" ? String(rowHazard) === "total" : String(rowHazard) === String(hazard);
  });
};
interface PhysicalRiskProps {
  id: string;
  data: PeaSummaryResponse;
  adaptedData?: PeaSummaryResponse;
  translationPrefix?: string;
  permissions?: PointDetailsPermissions;
  compareResultsMode?: CompareResultsMode;
  isLargeSite: boolean;
  tag?: React.ReactNode;
}
export const PhysicalRisk = ({
  id,
  data,
  adaptedData,
  translationPrefix = "portfolios.portfolioLevel.assetRisk",
  permissions,
  compareResultsMode,
  isLargeSite,
  tag
}: PhysicalRiskProps) => {
  const {
    t
  } = useTranslation("tools", {
    keyPrefix: translationPrefix
  });
  const {
    t: terms
  } = useTranslation("terms");
  const isPointLevel = translationPrefix.includes("pointLevel");
  const {
    hazard,
    rcp,
    year,
    setRcp
  } = useCollectionState();
  const isComparison = compareResultsMode === "compare" && !!adaptedData;
  const {
    canRunAutoAdaptation
  } = useAdaptationPermissions({
    isLargeSite
  });
  const rowsForHazard = useMemo(() => filterRowsByHazard(data, hazard), [data, hazard]);
  const adaptedRowsForHazard = useMemo(() => adaptedData ? filterRowsByHazard(adaptedData, hazard) : [], [adaptedData, hazard]);
  const [metric, setMetric] = useState<MetricKey>("avg_damage_mtd");
  const {
    metricOptions,
    selectedMetric
  } = useMetricOptions({
    metric,
    setMetric,
    showTtip: true,
    isPointLevel,
    permissions,
    translationPrefix
  });
  const hazardDataAvailable = isHazardMetricDataAvailable(hazard, metric);
  const hazardDataUnavailableMessage = t(`charts.${getMetricShortLabel(metric).toLowerCase()}.unavailableMessage`, "No Data Available");
  const buildChartDataIfAvailable = (rows: PeaSummaryResponse, field: MetricKey): ChartData => {
    if (!hazardDataAvailable) return {
      chartData: [],
      scenarios: []
    };
    return buildChartData(rows, field);
  };
  const {
    chartData,
    scenarios
  } = useMemo(() => buildChartDataIfAvailable(rowsForHazard, metric), [rowsForHazard, metric]);
  const {
    chartData: adaptedChartData
  } = useMemo(() => buildChartDataIfAvailable(adaptedRowsForHazard, metric), [adaptedRowsForHazard, metric]);
  const yAxisUnit = PERCENT_METRICS.includes(metric) ? "%" : "$";
  const formatValue = (value: number) => formatYAxisTick(metric, value);
  if (!metricOptions.length || !selectedMetric) return null;
  const renderDescription = () => {
    if (translationPrefix.includes("pointLevel")) return t("description");
    return canRunAutoAdaptation ? <Trans i18nKey={t(`description.${compareResultsMode}`)} values={{
      baseline: terms("adaptationTypes.baseline"),
      enhancedResilience: terms("adaptationTypes.enhanced_resilience")
    }} components={{
      span: <strong />
    }} data-sentry-element="Trans" data-sentry-component="renderDescription" data-sentry-source-file="PhysicalRisk.tsx" /> : t("description.default");
  };
  return <div id={id} className="grid grid-cols-12 @container/wrapper" data-sentry-component="PhysicalRisk" data-sentry-source-file="PhysicalRisk.tsx">
      <div className="col-span-12 mb-4 flex flex-col gap-1">
        <div className="flex items-start justify-between gap-2">
          <Header title={t("title")} tag={tag ?? (canRunAutoAdaptation && compareResultsMode && <AdaptationTag type={compareResultsMode} />)} data-sentry-element="Header" data-sentry-source-file="PhysicalRisk.tsx" />
        </div>
        <ContextualInfoCollapse data-sentry-element="ContextualInfoCollapse" data-sentry-source-file="PhysicalRisk.tsx">{renderDescription()}</ContextualInfoCollapse>
      </div>

      <div className="col-span-12 mb-0">
        <SelectMetricOptions variant="tabs" metric={metric} setMetric={setMetric} metricOptions={metricOptions} data-sentry-element="SelectMetricOptions" data-sentry-source-file="PhysicalRisk.tsx" />
      </div>

      <div className="col-span-12 grid grid-cols-12 gap-2 rounded-2xl rounded-tl-none border border-gray-200 bg-surface p-2 pt-4 shadow-sm @2xl/wrapper:p-4">
        {canRunAutoAdaptation && <div className="col-span-12 text-right @4xl/wrapper:col-span-4">
            <CollectionFiltersLabel withIcons />
          </div>}
        <div className="col-span-12 mb-2 @4xl/wrapper:order-first @4xl/wrapper:col-span-8">
          <ChartHeader title={selectedMetric.title} description={selectedMetric.description} contextualInfoButtonId="metrics" data-sentry-element="ChartHeader" data-sentry-source-file="PhysicalRisk.tsx" />
        </div>
        <div className="col-span-12 w-full rounded-xl border border-gray-100 px-2 py-4 @2xl/wrapper:p-4 @4xl/wrapper:col-span-8">
          <div className="h-[480px]">
            <ScenarioLineChart metric={selectedMetric.value} title={selectedMetric.label} description={selectedMetric.description} chartData={chartData} unavailableMessage={hazardDataUnavailableMessage} adaptedChartData={isComparison ? adaptedChartData : undefined} scenarios={scenarios} yAxisUnit={yAxisUnit} metricLabel={selectedMetric.shortLabel} compareResultsMode={compareResultsMode} canRunAutoAdaptation={canRunAutoAdaptation} data-sentry-element="ScenarioLineChart" data-sentry-source-file="PhysicalRisk.tsx" />
          </div>
        </div>

        <div className="col-span-12 @container/sub @4xl/wrapper:col-span-4">
          {scenarios.length < 1 ? <div className="flex h-full items-center justify-center text-gray-400">
              <Empty description="Scenario data not available" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div> : <>
              {canRunAutoAdaptation && compareResultsMode && scenarios.length > 0 && <div className="mb-1">
                  {isComparison ? <AdaptationTag type="baseline" /> : <AdaptationTag type={compareResultsMode} />}
                </div>}
              <ScenarioMiniRangeGrid metric={selectedMetric.value} chartData={chartData} scenarios={scenarios} rcp={rcp} year={year} yAxisUnit={yAxisUnit} scenarioColors={RCP_COLORS} formatValue={formatValue} onChangeRcp={setRcp} />

              {isComparison && <>
                  <div style={{
              color: ADAPTATION_TERM_COLORS.enhanced_resilience
            }} className="mt-2 font-semibold">
                    <ContextualInfoButtonWithText id="autoEnhancedResilience">
                      {terms("adaptationTypes.enhanced_resilience")}
                    </ContextualInfoButtonWithText>
                  </div>

                  <ScenarioMiniRangeGrid metric={selectedMetric.value} chartData={adaptedChartData} scenarios={scenarios} rcp={rcp} year={year} yAxisUnit={yAxisUnit} scenarioColors={RCP_COLORS} formatValue={formatValue} onChangeRcp={setRcp} />
                </>}
            </>}
        </div>
      </div>
    </div>;
};